import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Bot, Sparkles, UserPlus, Link2, Layers, BellRing, ShieldCheck,
  Crown, RefreshCw, Hash, Rocket,
} from "lucide-react";

const STEPS = [
  { icon: UserPlus, title: "Invite the bot", desc: "Add the VangTech Subs bot to your Discord server in one click — no coding, no config files." },
  { icon: Link2, title: "Connect your account", desc: "Link the bot to your VangTech Subs creator account so your tiers, members and payments stay in sync." },
  { icon: Layers, title: "Map tiers to roles", desc: "Pick which Discord role each subscription tier unlocks, and which channels are members-only." },
  { icon: Crown, title: "Members get instant access", desc: "When someone subscribes, the bot auto-assigns their role. Cancel or lapse, and access is removed automatically." },
];

const FEATURES = [
  { icon: RefreshCw, title: "Auto role sync", desc: "Roles are granted and revoked in real time as subscriptions start, renew, or cancel.", color: "bg-cyan-100" },
  { icon: Hash, title: "Members-only channels", desc: "Gate premium channels and categories behind paid tiers, hands-free.", color: "bg-lime-200" },
  { icon: ShieldCheck, title: "Stripe-powered billing", desc: "All payments run through your existing Stripe checkout — nothing new to set up.", color: "bg-amber-200" },
  { icon: BellRing, title: "Welcome & renewal DMs", desc: "The bot greets new members and nudges them before a renewal, so churn stays low.", color: "bg-rose-100" },
  { icon: Layers, title: "Run it from your dashboard", desc: "Manage tiers, perks and member access from the same Creator Dashboard you already use.", color: "bg-violet-100" },
  { icon: Crown, title: "Server-owner friendly", desc: "Built for Discord community and server owners who want to run memberships where their people already hang out.", color: "bg-indigo-100" },
];

export default function DiscordBot() {
  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="discord-bot-page">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b-2 border-[#111114] bg-indigo-600">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          >
            <span className="chip bg-lime-300 inline-flex items-center gap-1.5 mb-6" data-testid="bot-coming-soon-badge">
              <Sparkles className="w-3.5 h-3.5" /> Coming soon · In development
            </span>
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-white border-2 border-[#111114] flex items-center justify-center shadow-[4px_4px_0_0_#111114]">
              <Bot className="w-11 h-11 text-indigo-600" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mb-5">
              The VangTech Subs<br />Discord Bot
            </h1>
            <p className="text-indigo-100 text-lg max-w-2xl mx-auto mb-8">
              Invite one bot to your Discord community and run paid memberships right where your audience already is.
              Server owners can sell subscriptions and manage everyone straight from their dashboard.
            </p>
            <Button
              disabled
              className="bg-white/90 text-indigo-700 font-bold rounded-full px-7 py-6 text-base cursor-not-allowed opacity-80 border-2 border-[#111114]"
              data-testid="bot-invite-btn"
            >
              <Bot className="w-5 h-5 mr-2" /> Invite the bot — coming soon
            </Button>
            <p className="text-indigo-200 text-xs mt-4">We're building it now. Want early access? Join our Discord and we'll ping you.</p>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <p className="eyebrow text-indigo-600 mb-2">How it works</p>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#111114] mb-12">Four steps, fully automated</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="card-pop bg-white p-6 relative"
              data-testid={`bot-step-${i + 1}`}
            >
              <span className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-indigo-600 text-white font-display font-extrabold flex items-center justify-center border-2 border-[#111114]">{i + 1}</span>
              <s.icon className="w-8 h-8 text-indigo-600 mb-4" />
              <h3 className="font-display text-lg font-extrabold text-[#111114] mb-2">{s.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-white border-y-2 border-[#111114]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <p className="eyebrow text-indigo-600 mb-2">What it'll do</p>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#111114] mb-12">Built for Discord community owners</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`card-pop ${f.color} p-6`}
                data-testid={`bot-feature-${i + 1}`}
              >
                <f.icon className="w-8 h-8 text-[#111114] mb-4" />
                <h3 className="font-display text-lg font-extrabold text-[#111114] mb-2">{f.title}</h3>
                <p className="text-sm text-slate-700 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
        <Rocket className="w-10 h-10 text-indigo-600 mx-auto mb-5" />
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#111114] mb-4">In development — launching soon</h2>
        <p className="text-slate-600 text-lg mb-8 max-w-xl mx-auto">
          The Discord bot is actively being built. In the meantime you can already sell memberships and
          digital products from your VangTech Subs storefront today.
        </p>
        <Button
          disabled
          className="bg-indigo-600 text-white font-bold rounded-full px-7 py-6 text-base cursor-not-allowed opacity-80 border-2 border-[#111114]"
          data-testid="bot-cta-btn"
        >
          <Bot className="w-5 h-5 mr-2" /> Coming soon
        </Button>
      </section>

      <Footer />
    </div>
  );
}
